export const CHECKOUT_URL = 'https://postlaser-en.impultienda.ar/checkout/';

export const MOCKUP_URL =
  'https://cdn.magicpatterns.com/uploads/squHMvrYhE3BiPRoXdQLCj/MOCKUP_POSTLASER-EN.png';

export const AUTHOR_PHOTO_URL =
  'https://cdn.magicpatterns.com/uploads/3CkuFdj98EYX9NQEjdPTdR/MICHAEL_CARTER_-_IMPLANTES.png';

export const problems = [
  'Inconsistent documentation of post-treatment findings.',
  'Difficulty organizing symptoms by onset, severity, and progression.',
  'Uncertainty about which observations require closer assessment.',
  'Fragmented follow-up records.',
  'Incomplete communication during professional referrals.',
  "Time-consuming attempts to reconstruct the patient's clinical timeline.",
];

export const steps = [
  {
    key: 'capture',
    title: 'Capture',
    subtitle: 'Organize the post-treatment clinical picture.',
    body: 'Collect relevant procedure details, patient-reported symptoms, clinical observations, and available treatment records.',
    output: 'A structured post-laser assessment record.',
  },
  {
    key: 'classify',
    title: 'Classify',
    subtitle: 'Organize findings into meaningful assessment categories.',
    body: 'Document the timing, distribution, progression, and characteristics of observed changes.',
    output: 'A categorized clinical findings matrix.',
  },
  {
    key: 'flag',
    title: 'Flag',
    subtitle: 'Identify findings that warrant additional attention.',
    body: 'Use structured prompts to recognize potentially concerning developments and determine whether urgent assessment or specialist input may be appropriate.',
    output: 'A documented concern and escalation checklist.',
  },
  {
    key: 'document',
    title: 'Document',
    subtitle: 'Turn scattered observations into a professional clinical record.',
    body: 'Organize findings, clinical reasoning, communication, and decisions using practical documentation templates.',
    output: 'A structured assessment and decision record.',
  },
  {
    key: 'escalate',
    title: 'Escalate & Follow Up',
    subtitle: 'Create a clear communication and follow-up pathway.',
    body: 'Document referrals, safety-net instructions, follow-up arrangements, and unresolved concerns within your professional scope.',
    output: 'A documented escalation and follow-up plan.',
  },
];

export const beforeItems = [
  'Unorganized post-laser assessment notes.',
  'Inconsistent symptom documentation.',
  'No standardized method for recording clinical concerns.',
  'Fragmented referral communication.',
  'Follow-up information spread across multiple records.',
  'Uncertainty about whether all relevant information has been captured.',
];

export const afterItems = [
  'A repeatable five-step assessment workflow.',
  'Organized post-procedure observation records.',
  'Structured documentation of potentially concerning findings.',
  'Clear prompts for professional escalation decisions.',
  'Standardized referral communication templates.',
  'A practical system for documenting follow-up arrangements.',
];

export const volumes = [
  {
    number: '01',
    title: 'Post-Laser Assessment Workflow Blueprint™',
    tagline: 'Build your structured assessment process.',
    inside: [
      'Post-procedure assessment intake checklist.',
      'Procedure history documentation template.',
      'Patient-reported symptom collection form.',
      'Initial observation matrix.',
      'Five-step assessment workflow.',
      'Clinical information completeness checklist.',
    ],
    deliverable: 'A reusable post-laser assessment intake and documentation workflow.',
    note: '',
  },
  {
    number: '02',
    title: 'Laser Complication Recognition Matrix™',
    tagline: 'Organize clinical observations into structured assessment categories.',
    inside: [
      'Erythema and inflammatory observation matrix.',
      'Pigmentary change documentation framework.',
      'Blistering and skin integrity observation checklist.',
      'Healing progression documentation tools.',
      'Symptom onset and evolution matrix.',
      'Concerning finding documentation prompts.',
    ],
    deliverable: 'A categorized clinical observation matrix for post-laser presentations.',
    note: '',
  },
  {
    number: '03',
    title: 'Post-Laser Escalation Decision Pathway™',
    tagline: 'Support organized professional escalation decisions.',
    inside: [
      'Potential warning-sign recognition checklist.',
      'Escalation consideration matrix.',
      'Urgent assessment documentation prompts.',
      'Specialist referral preparation workflow.',
      'Scope-of-practice decision checkpoints.',
      'Clinical handoff information checklist.',
    ],
    deliverable: 'A structured escalation and referral documentation pathway.',
    note: 'This pathway supports professional judgment and does not replace emergency protocols, specialist assessment, or established clinical guidelines.',
  },
  {
    number: '04',
    title: 'Clinical Documentation & Case Tracking System™',
    tagline: 'Create consistent, traceable clinical records.',
    inside: [
      'Structured clinical assessment note.',
      'Symptom progression tracking sheet.',
      'Procedure parameter recording template.',
      'Clinical photography documentation checklist.',
      'Decision rationale documentation form.',
      'Follow-up record template.',
    ],
    deliverable: 'A reusable clinical documentation and case-tracking system.',
    note: '',
  },
  {
    number: '05',
    title: 'Post-Laser Follow-Up & Communication Blueprint™',
    tagline: 'Organize follow-up and professional communication.',
    inside: [
      'Follow-up planning checklist.',
      'Patient communication documentation template.',
      'Safety-net communication checklist.',
      'Referral communication framework.',
      'Unresolved concern tracking sheet.',
      'Follow-up completion checklist.',
    ],
    deliverable: 'A structured follow-up and communication workflow.',
    note: '',
  },
  {
    number: '06',
    title: 'Laser Complication Case Decision Toolkit™',
    tagline: 'Bring the complete framework together.',
    inside: [
      'Integrated five-step decision worksheet.',
      'Case organization templates.',
      'Assessment-to-escalation workflow.',
      'Clinical documentation audit checklist.',
      'Professional handoff preparation sheet.',
      'Case review and follow-up closure checklist.',
    ],
    deliverable: 'A complete, reusable post-laser complication assessment toolkit.',
    note: '',
  },
];

export const bonuses = [
  {
    number: '01',
    title: 'Post-Laser Red Flag Recognition Checklist™',
    body: 'A practical reference for organizing potentially concerning findings and documenting the need for further clinical assessment.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/b3da2c90-7cd4-4514-a78d-eb3175c09a07.jpg',
  },
  {
    number: '02',
    title: 'Laser Complication Documentation Templates™',
    body: 'Ready-to-use professional templates for clinical observations, symptom timelines, and decision records.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/d28900de-44c9-485e-8b03-ea79cf9741dd.jpg',
  },
  {
    number: '03',
    title: 'Post-Laser Follow-Up Tracker™',
    body: 'A structured tracking system for documenting patient-reported changes, follow-up arrangements, and outstanding clinical concerns.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/19301c41-96e3-4ac8-a781-c667a898a9d6.jpg',
  },
  {
    number: '04',
    title: 'Specialist Referral Handoff Checklist™',
    body: 'A practical checklist for organizing relevant information when communicating with another qualified healthcare professional.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/0fcbecb3-71cb-437c-ad76-272b35cf3714.jpg',
  },
  {
    number: '05',
    title: '15-Minute Clinical Workflow Organizer™',
    body: 'A one-page framework for assembling the core documentation components of a post-laser assessment.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/62bced70-bb45-4e90-a239-2dd7dd4c6ef5.jpg',
  },
];

export const packageItems = [
  '6 Practical Clinical Volumes',
  '5 Professional Bonuses',
  'LASER COMPLICATION DECISION MAP™',
  'Assessment Matrices',
  'Clinical Documentation Templates',
  'Escalation Checklists',
  'Follow-Up Tracking Tools',
  'Instant Digital Access',
  '7-Day Money-Back Guarantee',
];

export const insteadOf = [
  {
    instead: 'Lengthy theoretical chapters',
    get: 'Structured worksheets, decision matrices, documentation templates, and assessment checklists.',
  },
  {
    instead: 'Scattered clinical observations',
    get: 'A repeatable system for organizing findings and recording professional decisions.',
  },
  {
    instead: 'Inconsistent follow-up records',
    get: 'Standardized tools to document communication, referrals, and follow-up arrangements.',
  },
  {
    instead: 'Starting from scratch',
    get: 'A structured five-step professional framework.',
  },
];

export const audience = [
  'Qualified aesthetic medicine practitioners.',
  'Dermatology professionals.',
  'Licensed clinicians involved in aesthetic laser procedures.',
  'Qualified healthcare professionals responsible for post-treatment assessment.',
  'Clinical teams seeking standardized documentation workflows.',
];

export const startSteps = [
  { title: 'Get instant access', body: 'Complete your secure one-time purchase.' },
  { title: 'Download your clinical toolkit', body: 'Access the six practical volumes and five complementary resources.' },
  {
    title: 'Organize your workflow',
    body: 'Start with the five-step LASER COMPLICATION DECISION MAP™ and adapt the documentation tools to your clinical setting and applicable protocols.',
  },
  {
    title: 'Apply within your professional scope',
    body: 'Use the templates to support structured documentation, professional communication, and appropriate escalation.',
  },
];

export const testimonials = [
  {
    name: 'Dr. Isabella Vargas',
    role: 'Aesthetic Physician',
    location: 'Mexico City, Mexico',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/344988bf-8f8d-455f-bab4-ccb4e0298de3.jpg',
    quote:
      'Our follow-up notes used to live in three different places. The Decision Map gave our team one clear sequence — capture, classify, flag — and our post-laser records are finally consistent from one practitioner to the next.',
  },
  {
    name: 'Dr. Andrés Molina',
    role: 'Dermatologist',
    location: 'Bogotá, Colombia',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/094b7a5c-cec1-46c4-8379-5a447ebd87a2.jpg',
    quote:
      'The referral handoff checklist alone was worth it. When I need specialist input, I now send a complete, organized timeline instead of reconstructing it from memory.',
  },
  {
    name: 'Laura Ortiz, RN',
    role: 'Aesthetic Nurse Practitioner',
    location: 'Madrid, Spain',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/7f2fcaae-e271-45d7-ac72-9083b275f9de.jpg',
    quote:
      'I adapted the templates to our clinic protocol in one afternoon. The symptom onset matrix makes it so much easier to document progression clearly at every review.',
  },
  {
    name: 'Dr. Ricardo Salinas',
    role: 'Aesthetic Medicine Practitioner',
    location: 'Santiago, Chile',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/6ffa3a90-e392-417d-95d5-fa969e7b39a0.jpg',
    quote:
      'Practical, not theoretical. The escalation checkpoints help my team document why each decision was made — exactly what we needed for our case reviews.',
  },
];

export const faqs = [
  {
    q: 'Is this a theoretical laser medicine textbook?',
    a: 'No. This is a practical professional workflow resource focused on assessment organization, documentation, escalation considerations, and follow-up communication.',
  },
  {
    q: 'Does the blueprint teach laser procedures?',
    a: 'No. Its primary purpose is to support the organization of post-treatment complication assessments. It does not provide procedural certification or replace supervised clinical training.',
  },
  {
    q: 'Can I use the templates with my existing clinical workflow?',
    a: 'Yes. The tools are designed to be adaptable to professional documentation practices, institutional protocols, and applicable clinical requirements.',
  },
  {
    q: 'Does the blueprint diagnose laser complications?',
    a: 'No. The resource supports structured observation and documentation. Diagnosis and management require qualified clinical judgment and appropriate professional evaluation.',
  },
  {
    q: 'Is it suitable for beginners?',
    a: 'It is intended for qualified healthcare professionals with relevant clinical training. It is not a substitute for foundational education or professional licensing.',
  },
  {
    q: 'Will I receive the bonuses?',
    a: 'Yes. The complete package includes six practical volumes and five complementary professional resources.',
  },
  { q: 'Is this a subscription?', a: 'No. Your $27 purchase is a one-time payment.' },
  { q: 'How will I receive the product?', a: 'You will receive digital access after completing your purchase.' },
  { q: 'What if I am not satisfied?', a: 'You can request a refund within seven days under the stated guarantee.' },
];

export const buyers = [
  { name: 'Valentina R.', country: 'Colombia' },
  { name: 'Carlos M.', country: 'Mexico' },
  { name: 'Lucía G.', country: 'Argentina' },
  { name: 'Javier P.', country: 'Spain' },
  { name: 'Camila S.', country: 'Chile' },
  { name: 'Diego F.', country: 'Peru' },
  { name: 'Sofía H.', country: 'Ecuador' },
  { name: 'Mateo L.', country: 'Uruguay' },
  { name: 'Daniela C.', country: 'Costa Rica' },
  { name: 'Alejandro V.', country: 'Dominican Republic' },
  { name: 'Mariana T.', country: 'Guatemala' },
  { name: 'Sebastián A.', country: 'Panama' },
  { name: 'Gabriela N.', country: 'Venezuela' },
  { name: 'Andrea B.', country: 'Bolivia' },
  { name: 'Fernando J.', country: 'Paraguay' },
];
